import { toast } from '@affine/component';
import {
  automationDeleteMutation,
  automationRestoreMutation,
  automationTrashMutation,
} from '@affine/graphql';
import { I18n } from '@affine/i18n';
import { OnEvent, Service } from '@toeverything/infra';
import { nanoid } from 'nanoid';

import type { ContainerCopyBeforeDeleteService } from '../../../components/delete-with-copy/container-copy-before-delete.service';
import type { WorkspaceServerService } from '../../cloud';
import {
  classifyLifecycleMutationError,
  OFFLINE_DELETE_COPY_CONFIRMATION,
} from '../../offline-mutation-outbox/services/offline-mutation-error';
import {
  type OfflineMutationOutboxService,
  OfflineMutationTransientError,
} from '../../offline-mutation-outbox/services/offline-mutation-outbox';
import {
  OFFLINE_DELETION_HOLD_MS,
  type OfflineMutationRecord,
} from '../../offline-mutation-outbox/types';
import type { Workspace, WorkspaceService } from '../../workspace';
import { WorkspaceInitialized } from '../../workspace/events';
import type {
  BlockDeletionPresentationService,
  BlockLifecycleRecord,
} from './block-deletion-presentation';
import type { WorkflowService } from './workflow';

/**
 * An automation of a cloud workspace goes to the trash, comes back and
 * leaves for good through the offline mutation outbox, like a table: queued
 * at once, sent when online, confirmed by the server's receipt. The server
 * moves the block in its page and the config in one commit; the client never
 * changes the host doc before the receipt.
 */

export type AutomationDeletionTarget = { docId: string; blockId: string };
type AutomationLifecycleOperation =
  | 'automation.trash'
  | 'automation.restore'
  | 'automation.permanentDelete';
type AutomationLifecycleRecord = Extract<
  BlockLifecycleRecord,
  { operation: AutomationLifecycleOperation }
>;

const AUTOMATION_TRASH_BLOCKED = 'AUTOMATION_TRASH_BLOCKED';
const AUTOMATION_DELETE_FORBIDDEN = 'AUTOMATION_DELETE_FORBIDDEN';
/**
 * Codes of a refusal: the server will not do it, so the record stops. Every
 * other error (settling, an internal error, no code, the network) retries
 * under the same operation id.
 */
const REFUSAL_CODES = [
  AUTOMATION_TRASH_BLOCKED,
  AUTOMATION_DELETE_FORBIDDEN,
  'AUTOMATION_CONFIG_NOT_FOUND',
  'OPERATION_TARGET_MISMATCH',
  'FORBIDDEN',
  'BAD_REQUEST',
  'NOT_FOUND',
] as const;

/** What the user reads when the server refuses, by failure class or code. */
const REFUSAL_MESSAGE: Readonly<Record<string, string>> = {
  [AUTOMATION_DELETE_FORBIDDEN]: 'com.streamlain.trash.automation-forbidden',
  FORBIDDEN: 'com.streamlain.trash.automation-forbidden',
  automation_trash_host_trashed: 'com.streamlain.trash.automation-host-trashed',
  automation_restore_occupied:
    'com.streamlain.trash.automation-restore-occupied',
};
const REFUSED: Record<AutomationLifecycleOperation, string> = {
  'automation.trash': 'com.streamlain.trash.automation-trash-failed',
  'automation.restore': 'com.streamlain.trash.restore-failed',
  'automation.permanentDelete': 'com.streamlain.trash.automation-delete-failed',
};
const MUTATION = {
  'automation.trash': automationTrashMutation,
  'automation.restore': automationRestoreMutation,
  'automation.permanentDelete': automationDeleteMutation,
} as const;

@OnEvent(
  WorkspaceInitialized,
  (service: AutomationDeletionService) => service.onWorkspaceInitialized
)
export class AutomationDeletionService extends Service {
  constructor(
    private readonly workspaceService: WorkspaceService,
    private readonly workspaceServerService: WorkspaceServerService,
    private readonly outbox: OfflineMutationOutboxService,
    private readonly presentation: BlockDeletionPresentationService,
    private readonly workflows: WorkflowService,
    private readonly copyBeforeDelete: ContainerCopyBeforeDeleteService
  ) {
    super();
    this.disposables.push(
      this.outbox.registerExecutor('automation.trash', mutation =>
        this.send(mutation)
      ),
      this.outbox.registerExecutor('automation.restore', mutation =>
        this.send(mutation)
      ),
      this.outbox.registerExecutor('automation.permanentDelete', mutation =>
        this.send(mutation)
      )
    );
    const failedSubscription = this.outbox.failed$.subscribe(mutation => {
      if (isAutomationLifecycleMutation(mutation)) this.onFailed(mutation);
    });
    this.disposables.push(() => failedSubscription.unsubscribe());
  }

  onWorkspaceInitialized(_workspace: Workspace): void {
    // The event decorator constructs this service when the workspace opens,
    // so an automation lifecycle queued before a reload is sent again.
  }

  /**
   * Queues the trash of an automation. A restore of it not yet sent is
   * withdrawn instead, and nothing is sent.
   *
   * @returns the queued request, or null when a queued restore was withdrawn
   */
  async requestTrash(
    target: AutomationDeletionTarget,
    copy: { copyConfirmed: boolean; displayName: string }
  ): Promise<{ mutationId: string } | null> {
    if (await this.withdrawUnsent('automation.restore', target)) return null;
    const mutation = await this.outbox.enqueue({
      operation: 'automation.trash',
      payload: { ...lifecyclePayload(target), ...copy },
      idempotencyKey: `automation.trash:${target.docId}:${target.blockId}`,
      dedupePending: true,
      holdMs: OFFLINE_DELETION_HOLD_MS,
      orderKey: automationOrderKey(target),
    });
    this.presentation.announceQueued(mutation.id);
    return { mutationId: mutation.id };
  }

  /**
   * Queues the restore of a trashed automation. A trash of it not yet sent
   * is withdrawn instead, and nothing is sent.
   *
   * @returns the queued request, or null when a queued trash was withdrawn
   */
  async requestRestore(
    target: AutomationDeletionTarget
  ): Promise<{ mutationId: string } | null> {
    if (await this.withdrawUnsent('automation.trash', target)) return null;
    const mutation = await this.outbox.enqueue({
      operation: 'automation.restore',
      payload: lifecyclePayload(target),
      idempotencyKey: `automation.restore:${target.docId}:${target.blockId}`,
      dedupePending: true,
      orderKey: automationOrderKey(target),
    });
    return { mutationId: mutation.id };
  }

  /** Queues the permanent delete of a trashed automation. */
  async requestPermanentDelete(
    target: AutomationDeletionTarget
  ): Promise<{ mutationId: string }> {
    const mutation = await this.outbox.enqueue({
      operation: 'automation.permanentDelete',
      payload: lifecyclePayload(target),
      idempotencyKey: `automation.permanentDelete:${target.docId}:${target.blockId}`,
      dedupePending: true,
      holdMs: OFFLINE_DELETION_HOLD_MS,
      orderKey: automationOrderKey(target),
    });
    return { mutationId: mutation.id };
  }

  /**
   * Withdraws the queued counter operation of the same automation through
   * the outbox's atomic cancel; true when one will never be sent.
   */
  private async withdrawUnsent(
    operation: 'automation.trash' | 'automation.restore',
    target: AutomationDeletionTarget
  ): Promise<boolean> {
    let withdrawn = false;
    for (const mutation of await this.outbox.refreshPendingMutations()) {
      if (
        mutation.operation === operation &&
        isAutomationLifecycleMutation(mutation) &&
        mutation.payload.docId === target.docId &&
        mutation.payload.blockId === target.blockId &&
        (await this.outbox.cancelPending(mutation.id))
      ) {
        withdrawn = true;
      }
    }
    return withdrawn;
  }

  private async send(mutation: AutomationLifecycleRecord): Promise<void> {
    const gql = this.workspaceServerService.server?.gql;
    if (!gql) {
      throw new OfflineMutationTransientError(
        'No server for the automation lifecycle'
      );
    }
    const { docId, blockId, operationId } = mutation.payload;
    try {
      if (mutation.operation === 'automation.trash') {
        await this.copyBeforeDelete.checkAutomationTrashBeforeSend(mutation);
      }
      await this.workspaceService.workspace.engine.doc.flushAndWaitForSynced(
        docId
      );
      await gql({
        query: MUTATION[mutation.operation],
        variables: {
          input: {
            workspaceId: mutation.workspaceId,
            docId,
            blockId,
            operationId,
          },
        },
      });
    } catch (error) {
      if (
        error instanceof Error &&
        error.message === OFFLINE_DELETE_COPY_CONFIRMATION
      )
        throw error;
      throw classifyLifecycleMutationError(
        error,
        AUTOMATION_TRASH_BLOCKED,
        REFUSAL_CODES
      );
    }
    if (mutation.operation === 'automation.permanentDelete') {
      // The usage lists of the workflows name the automation.
      await this.workflows.refreshWorkflows();
    }
  }

  /**
   * A refusal shows once and leaves; an unconfirmed request and one waiting
   * for the copy question stay.
   */
  private onFailed(mutation: AutomationLifecycleRecord): void {
    if (
      mutation.failure !== 'refused' ||
      mutation.lastError === OFFLINE_DELETE_COPY_CONFIRMATION
    )
      return;
    toast(
      I18n.t(
        REFUSAL_MESSAGE[mutation.lastError ?? ''] ?? REFUSED[mutation.operation]
      )
    );
    this.outbox.removeFailed(mutation.id).catch(error => {
      console.error('[AutomationDeletion] Removing a refusal failed:', error);
    });
  }
}

function lifecyclePayload({ docId, blockId }: AutomationDeletionTarget) {
  return { docId, blockId, operationId: nanoid() };
}

/** Trash, restore and delete of one automation leave in queue order. */
function automationOrderKey({ blockId }: AutomationDeletionTarget): string {
  return `automation:${blockId}`;
}

function isAutomationLifecycleMutation(
  mutation: OfflineMutationRecord
): mutation is AutomationLifecycleRecord {
  return (
    mutation.operation === 'automation.trash' ||
    mutation.operation === 'automation.restore' ||
    mutation.operation === 'automation.permanentDelete'
  );
}
